import { useNavigate } from "react-router-dom";
import type { Projeto } from "@/types/ui-models";
import ProjectStatusBadge from "./ProjectStatusBadge";
import ProjectProgressBar from "./ProjectProgressBar";

interface ProjectCardProps {
  projeto: Projeto;
}

export default function ProjectCard({ projeto }: ProjectCardProps) {
  const navigate = useNavigate();

  const prioridadeBorder = {
    normal: "border-background-200",
    alta: "border-warning-300",
    critica: "border-danger-300",
  };

  return (
    <div className={`bg-white rounded-card border ${prioridadeBorder[projeto.prioridade]} p-5 hover:shadow-md transition-all duration-200`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading truncate" title={projeto.nome}>
            {projeto.nome}
          </h3>
          {projeto.municipio && (
            <p className="text-xs text-foreground-500 mt-0.5">{projeto.municipio}{projeto.uf ? `/${projeto.uf}` : ""}</p>
          )}
        </div>
        <ProjectStatusBadge status={projeto.status} />
      </div>

      {/* Type */}
      <div className="flex items-center gap-2 text-xs text-foreground-600 mb-3 flex-wrap">
        <span className="bg-background-100 px-2 py-0.5 rounded-md">{projeto.tipo}</span>
      </div>

      {/* Client + Responsible */}
      {(projeto.cliente || projeto.responsavel) && (
        <div className="space-y-1 mb-4">
          {projeto.cliente && (
            <div className="flex items-center gap-1.5 text-xs text-foreground-600">
              <i className="ri-building-line text-foreground-400"></i>
              <span className="truncate">{projeto.cliente}</span>
            </div>
          )}
          {projeto.responsavel && (
            <div className="flex items-center gap-1.5 text-xs text-foreground-600">
              <i className="ri-user-line text-foreground-400"></i>
              <span>Resp.: {projeto.responsavel}</span>
            </div>
          )}
        </div>
      )}

      {/* Dates */}
      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
        <span>Início: {new Date(projeto.dataInicio).toLocaleDateString("pt-BR")}</span>
        {projeto.dataPrevisao && (
          <>
            <span>·</span>
            <span>Previsão: {new Date(projeto.dataPrevisao).toLocaleDateString("pt-BR")}</span>
          </>
        )}
      </div>

      {/* Progress */}
      <div className="mb-4">
        <ProjectProgressBar progresso={projeto.progresso} size="sm" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-background-50 rounded-lg">
          <p className="text-sm font-bold text-foreground-900 tabular-nums">{projeto.totalArvores.toLocaleString("pt-BR")}</p>
          <p className="text-[10px] text-foreground-500">Exemplares</p>
        </div>
        <div className="text-center p-2 bg-background-50 rounded-lg">
          <p className="text-sm font-bold text-foreground-900 tabular-nums">{projeto.vistoriasRealizadas.toLocaleString("pt-BR")}</p>
          <p className="text-[10px] text-foreground-500">Vistorias</p>
        </div>
        <div className="text-center p-2 bg-background-50 rounded-lg">
          <p className="text-sm font-bold text-foreground-900 tabular-nums">{projeto.especiesIdentificadas.toLocaleString("pt-BR")}</p>
          <p className="text-[10px] text-foreground-500">Espécies</p>
        </div>
      </div>

      {/* Alertas */}
      {projeto.alertas.length > 0 && (
        <div className="mb-4 space-y-1">
          {projeto.alertas.slice(0, 2).map((alerta, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-warning-600 bg-warning-50 rounded-md px-2 py-1">
              <i className="ri-error-warning-line shrink-0 mt-0.5 text-[10px]"></i>
              <span className="line-clamp-1">{alerta}</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(`/projetos/${projeto.id}`)}
          className="btn-primary text-xs px-3 py-1.5 flex-1 cursor-pointer"
        >
          <i className="ri-folder-open-line"></i>
          Abrir
        </button>
        <button
          onClick={() => navigate(`/mapa?projeto=${projeto.id}`)}
          className="btn-outline text-xs px-3 py-1.5 cursor-pointer"
          title="Ver no mapa"
        >
          <i className="ri-map-2-line"></i>
        </button>
        <button
          onClick={() => navigate(`/relatorios?projeto=${projeto.id}`)}
          className="btn-outline text-xs px-3 py-1.5 cursor-pointer"
          title="Relatório"
        >
          <i className="ri-file-chart-line"></i>
        </button>
      </div>
    </div>
  );
}